"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.f1EngineAdapters = void 0;
const common_1 = require("@finley-technologies/common");
const comparison_1 = require("./comparison");
const index_1 = require("./index");
// ----- TEMPORARY ADAPTERS (DELETE FILE AFTER MIGRATION) -----
// Prevents throwing runtime exceptions at time of building engine
// Failures to initialize the engine are likely limited to missing formula implementations or invalid syntax tree construction
const safeInitializeEngine = ({ formulas, }) => {
    // Default engine interface, automatically throw on evaluation
    let nodeEngine = {
        rangeDataRequired: false,
        sanitizeRangeData: () => ({}), // No sanitization on failure
        evaluate: () => {
            throw new Error('Formula engine was not initialized');
        },
    };
    // Safely initialize
    try {
        nodeEngine = (0, index_1.initializeEngine)({ formulas });
    }
    catch {
        // no-op
    }
    return nodeEngine;
};
const hyperEngineAdapter = (formulas) => {
    const nodeEngine = safeInitializeEngine({ formulas });
    let currentFormulaInput;
    const evaluate = (formula) => {
        const { output } = nodeEngine.evaluate({
            formula,
            formulaInput: currentFormulaInput,
        });
        return output.result;
    };
    return {
        compare: (formula, expectedResult) => {
            try {
                const f1Result = evaluate(formula);
                const match = f1Result === expectedResult;
                (0, comparison_1.updateComparisonCache)({
                    formula,
                    result: match ? 'match' : 'mismatch',
                });
            }
            catch {
                (0, comparison_1.updateComparisonCache)({ formula, result: 'error' });
            }
        },
        // Enhance existing input if required for dependent field evaluation
        // Otherwise, cached input is replaced
        setVariables: (input, options) => {
            if (options?.addToCurrentInput) {
                const updated = currentFormulaInput ?? {};
                for (const [key, value] of Object.entries(input)) {
                    updated[key] = value;
                }
                currentFormulaInput = updated;
            }
            else {
                currentFormulaInput = input;
            }
        },
        // Copy/paste from HyperEngine
        evaluate: (formula) => {
            return evaluate(formula);
        },
        // Copy/paste from HyperEngine
        evaluateBoolean: (formula) => {
            const res = evaluate(formula);
            if (typeof res === 'boolean')
                return res;
            throw Error(`expected boolean result but got [${res}] from formula ${formula}`);
        },
        // Copy/paste from HyperEngine
        evaluateOptionalBoolean: (formula) => {
            const res = evaluate(formula);
            if (res === undefined || res === null)
                return res;
            if (typeof res === 'boolean')
                return res;
            throw Error(`expected optional boolean result but got [${res}] from formula ${formula}`);
        },
        // Copy/paste from HyperEngine
        evaluateNumber: (formula) => {
            const res = evaluate(formula);
            if (typeof res === 'number')
                return res;
            throw Error(`expected number result but got [${res}] from formula ${formula}`);
        },
        // Copy/paste from HyperEngine
        evaluateFullDateStr: (formula) => {
            const res = evaluate(formula);
            if (common_1.typeguards.isFullDateStr(res))
                return res;
            throw Error(`expected FullDateStr result but got [${res}] from formula ${formula}`);
        },
        // Copy/paste from HyperEngine
        evaluateOptionalNumber: (formula) => {
            const res = evaluate(formula);
            if (res === undefined || res === null)
                return res;
            if (typeof res === 'number')
                return res;
            throw Error(`expected optional number result but got [${res}] from formula ${formula}`);
        },
    };
};
const excelEngineAdapter = ({ formulas, data, }) => {
    const nodeEngine = safeInitializeEngine({ formulas });
    if (nodeEngine.rangeDataRequired) {
        data.forEach((row) => nodeEngine.sanitizeRangeData({ row }));
    }
    let currentFormulaInput;
    const evaluate = (formula) => {
        const { output } = nodeEngine.evaluate({
            formula,
            formulaInput: currentFormulaInput,
        });
        return output.result;
    };
    return {
        compare: (formula, expectedResult) => {
            try {
                const f1Result = evaluate(formula);
                const match = f1Result === expectedResult;
                (0, comparison_1.updateComparisonCache)({
                    formula,
                    result: match ? 'match' : 'mismatch',
                });
            }
            catch {
                (0, comparison_1.updateComparisonCache)({ formula, result: 'error' });
            }
        },
        setVariables: (input) => {
            currentFormulaInput = input;
            // Existing engine returns list of variable names so that they can be cleared
            // New engine does not need to do that, return empty array
            return [];
        },
        clearVariables: () => { },
        clearSheet: () => { },
        evaluateNumber: (formula) => {
            const result = evaluate(formula);
            (0, common_1.finleyAssert)(typeof result === 'number', new common_1.error.FinleyError('Failed to evaluate formula to expected number type', {
                metadata: {
                    logMeta: {
                        formula,
                        result,
                        typeof: typeof result,
                    },
                },
            }));
            return result;
        },
        evaluateFullDateStr: (formula) => {
            const result = evaluate(formula);
            (0, common_1.finleyAssert)(common_1.typeguards.isFullDateStr(result), new common_1.error.FinleyError('Failed to evaluate formula to expected fullDateStr type', {
                metadata: {
                    logMeta: {
                        formula,
                        result,
                        typeof: typeof result,
                    },
                },
            }));
            return result;
        },
        evaluateBoolean: (formula) => {
            const result = evaluate(formula);
            (0, common_1.finleyAssert)(typeof result === 'boolean', new common_1.error.FinleyError('Failed to evaluate formula to expected boolean type', {
                metadata: {
                    logMeta: {
                        formula,
                        result,
                        typeof: typeof result,
                    },
                },
            }));
            return result;
        },
    };
};
exports.f1EngineAdapters = {
    hyperEngine: hyperEngineAdapter,
    excelEngine: excelEngineAdapter,
};
//# sourceMappingURL=adapter.js.map